/**
 * How the Reports page writes a figure. One place, so a rate reads the same in a card, a table
 * cell, a chart tooltip and a table fallback.
 *
 * Digits are Latin and dates Gregorian (yyyy-MM-dd), as everywhere else in the panel. A figure
 * that does not exist is an em dash — never a zero, which would be a finding.
 */

export const DASH = '—'

export const num = (value) => (value == null ? DASH : Number(value).toLocaleString('en-US'))

/** One decimal, the way the server rounds. */
export const decimal = (value) =>
  value == null ? DASH : Number(value).toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 0 })

export const pct = (value) => (value == null ? DASH : `${decimal(value)}%`)

/** `12 / 340` — the counts behind a rate. */
export const ofN = (rate) =>
  rate?.denominator == null ? DASH : `${num(rate.numerator)} / ${num(rate.denominator)}`

/** yyyy-MM-dd from an ISO timestamp, without moving it through a time zone. */
export const day = (iso) => (typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : DASH)

/** HH:mm from an ISO timestamp. */
export const clock = (iso) => (typeof iso === 'string' && iso.length >= 16 ? iso.slice(11, 16) : '')

/**
 * Which way a change went, as a word as well as a sign — "down 3.1 pts" — so it is never
 * carried by colour or an arrow alone.
 *
 * @returns {{ direction: 'up'|'down'|'flat', points: string, favourable: boolean|null } | null}
 */
export function change(delta, higherIsBetter = true) {
  if (delta == null) return null
  const value = Number(delta)
  if (value === 0) return { direction: 'flat', points: decimal(0), favourable: null }
  const up = value > 0
  return {
    direction: up ? 'up' : 'down',
    points: decimal(Math.abs(value)),
    favourable: up === higherIsBetter,
  }
}

/** Sort value for a rate cell: suppressed and empty cells sort below every real rate. */
export const rateSort = (rate) => (rate?.suppressed || rate?.value == null ? -1 : Number(rate.value))

/** A rate's reading for a chart: null where it is withheld or there is nothing to read. */
export const rateValue = (rate) => (rate?.suppressed || rate?.value == null ? null : Number(rate.value))

/**
 * One rate's readings across rows, oldest first — the sparkline on a headline tile.
 *
 * @param {object[]} rows
 * @param {string} key  the row's RateDto property
 * @param {string} [dateKey]  sort by this ISO date first; omitted, the rows' own order stands
 */
export const readings = (rows, key, dateKey) =>
  [...(rows ?? [])]
    .sort((a, b) => (dateKey ? String(a[dateKey] ?? '').localeCompare(String(b[dateKey] ?? '')) : 0))
    .map((row) => rateValue(row[key]))
